import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const API_BASE_URL = "https://bank-converter-app-3.onrender.com"; // ✅ correct URL

const ConsolidateExport = ({ authToken, documentHistory = [] }) => {
  const [documentType, setDocumentType] = useState("transaction");
  const [exportFormat, setExportFormat] = useState("csv");
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const filteredDocuments = Array.isArray(documentHistory)
    ? documentHistory.filter(doc =>
        documentType === "transaction"
          ? doc.document_type === "bank_statement"
          : doc.document_type === "invoice"
      )
    : [];

  const handleIdToggle = (docId) => {
    setSelectedIds((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredDocuments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredDocuments.map((doc) => doc.id));
    }
  };

  const handleConsolidate = async () => {
    if (selectedIds.length === 0) {
      Swal.fire("Selection Required", "Please select at least one file to consolidate.", "warning");
      return;
    }

    setLoading(true);
    const payload = {
      document_ids: selectedIds,
      document_type: documentType,
      export_format: exportFormat,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/export/consolidated`, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      });

      const ext = exportFormat === "excel" ? "xlsx" : "csv";
      const filename = `Consolidated_${documentType}.${ext}`;
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      Swal.fire("Success", "Consolidated file downloaded successfully!", "success");
    } catch (error) {
      console.error("Consolidation failed:", error);
      Swal.fire("Error", "Failed to consolidate files.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIds([]);
  }, [documentType]);

  const styles = {
    container: { padding: "20px", backgroundColor: "#fff", borderRadius: "10px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" },
    optionsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "30px" },
    select: { width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" },
    fileList: { maxHeight: "400px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "5px", marginBottom: "30px" },
    fileItem: { display: "flex", alignItems: "center", padding: "10px 5px", borderBottom: "1px solid #f9f9f9", gap: "10px" },
    actionButton: { width: "100%", padding: "15px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "5px", fontSize: "1.1em", cursor: "pointer" },
  };

  return (
    <div style={styles.container}>
      <h2>📊 Consolidated Export</h2>
      <p>Select multiple files below to merge them into one export file.</p>

      {/* Options */}
      <div style={styles.optionsGrid}>
        <div>
          <label>Data Type:</label>
          <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} style={styles.select}>
            <option>Choose type of file</option>
            <option value="transaction">Bank Transactions</option>
            <option value="invoice">Invoices</option>
          </select>
        </div>
        <div>
          <label>Export Format:</label>
          <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} style={styles.select}>
            <option value="csv">CSV (.csv)</option>
            <option value="excel">Excel (.xlsx)</option>
          </select>
        </div>
      </div>

      {/* File list */}
      <h3>Select Files ({filteredDocuments.length})</h3>
      <button onClick={handleSelectAll} disabled={filteredDocuments.length === 0 || loading}>
        {selectedIds.length === filteredDocuments.length ? "Deselect All" : "Select All"}
      </button>

      <div style={styles.fileList}>
        {filteredDocuments.length === 0 ? (
          <p>No files found.</p>
        ) : (
          filteredDocuments.map((doc) => (
            <div key={doc.id} style={styles.fileItem}>
              <input
                type="checkbox"
                checked={selectedIds.includes(doc.id)}
                onChange={() => handleIdToggle(doc.id)}
              />
              <label>{doc.filename} ({new Date(doc.upload_date).toLocaleDateString()})</label>
            </div>
          ))
        )}
      </div>

      <button onClick={handleConsolidate} style={styles.actionButton} disabled={loading || selectedIds.length === 0}>
        {loading ? "Processing..." : `Export ${selectedIds.length} Files`}
      </button>
    </div>
  );
};

export default ConsolidateExport;
